import {inject, Injectable, WritableSignal} from '@angular/core';
import {Router} from '@angular/router';
import {IFaceMatchValidation, IField, ValidationType} from '@core/api/data-access';
import {ISignCycle, withIdbPersistence} from '@global-data';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {SignalStoreNames} from '@shared-types';
import {innerSignal} from '@shared/signals-extension';

import {livenessValidatorTestFactory} from '../logic';
import {ValidationGroupModel} from '../models';
import {IBaseValidationTest, IFaceMatchValidatorTest, ILivenessValidatorTest} from '../schemas';
import {livenessValidatorInitialState} from './liveness-validator-store.types';

const ValidatorStoreClass = signalStore(
	{providedIn: 'root', protectedState: false},
	// Idb Sync
	withIdbPersistence(SignalStoreNames.LIVENESS_VALIDATOR, livenessValidatorInitialState),
	// State
	withState(livenessValidatorInitialState),
	// Computed
	withComputed(store => ({})),
	//	Methods
	withMethods(store => ({})),
	//	Hooks
	withHooks(store => ({})),
);

@Injectable({
	providedIn: 'root',
})
export class ValidatorStore extends ValidatorStoreClass {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */
	private static readonly validationTypeOrder: ValidationType[] = [ValidationType.Liveness, ValidationType.DocumentVerification, ValidationType.FaceMatch];

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */
	private readonly router: Router = inject(Router);
	/* ------------------------------------------------  Signals ------------------------------------------------ */

	/* -------------------------------------------------- Data -------------------------------------------------- */
	public readonly allPassed: () => boolean = () => this.tests()?.every(test => test.results.passed);
	public readonly getActiveTest: () => IBaseValidationTest = () => this.tests().find(test => !test.results.passed);
	public readonly hasTests: () => boolean = () => Boolean(this.tests()?.length);
	public readonly neededFields: () => number[] = () => {
		const tests = this.tests();
		return (
			tests !== null &&
			tests?.flatMap(test =>
				test.schema.type === ValidationType.FaceMatch && (test.schema as IFaceMatchValidation)?.imageToCompare?.metaData ? [(test.schema as IFaceMatchValidation)?.imageToCompare?.metaData] : [],
			)
		);
	};
	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {
		super();
	}
	/* ------------------------------------------------  Methods ------------------------------------------------ */
	public reset(): void {
		patchState(this, livenessValidatorInitialState);
	}

	public rebuildTests(tests = this.tests()) {
		if (tests) {
			const newTests = tests.map(test => {
				if (Reflect.has(test, 'as')) return test;
				const t: IBaseValidationTest = {
					...test,
					as<T extends IBaseValidationTest>(): T {
						return this;
					},
					isValid(): boolean {
						return this.results.passed === true;
					},
				};
				return t;
			});
			patchState(this, {tests: newTests});
		}
	}

	public init(validationGroup: ValidationGroupModel, signCycleModel: WritableSignal<ISignCycle>): {hasTests: boolean; neededFields: number[]} {
		// set the sign cycle model
		patchState(this, {signCycleModel$: signCycleModel});
		// store the tests
		const tests = validationGroup.validations.map(validation => livenessValidatorTestFactory(validation, signCycleModel));
		tests.sort((a, b) => ValidatorStore.validationTypeOrder.findIndex(type => type === a.schema.type) - ValidatorStore.validationTypeOrder.findIndex(type => type === b.schema.type));
		patchState(this, {tests});
		return {
			hasTests: this.hasTests(),
			neededFields: this.neededFields(),
		};
	}

	public initTestFields(fields: IField[]): void {
		// store the fields on relevant tests
		const tests = this.tests();
		const testsWithFields = tests.map(test => {
			switch (test.schema.type) {
				case ValidationType.FaceMatch:
					const schema: IFaceMatchValidation = test.schema as IFaceMatchValidation;
					const index = schema?.imageToCompare?.metaData;
					if (index) {
						const field = fields.find(field => field.index === index);
						schema.imageData = field?.value;
					}
					break;
			}
			return test;
		});

		patchState(this, {tests: testsWithFields});
	}

	public startValidator(): void {
		if (this.allPassed()) return;
		if (this.getActiveTest())
			this.navigateToNextTest().then(() => {
				console.debug('Navigated to next test');
			});
	}

	public setTestResults<T extends IBaseValidationTest>(test: T, results: T['results']['resultData'], navigateToNextTest = true): void {
		if (!test) throw new Error('[ValidatorStore] Test not found');
		const validationType = test.schema.type;
		if (!validationType) throw new Error('[ValidatorStore] Test type not found');
		test.results.resultData = results;
		test.results.passed = test.isValid();

		// update the test in the store
		this.patchTest(test);

		// navigate to the next test
		if (navigateToNextTest && test.results.passed) {
			this.navigateToNextTest().then(() => {
				console.debug('Navigated to next test');
			});
		}
	}

	public getValidationTestBy(by: ExactlyOne<{type: ValidationType; uid: string}>): IBaseValidationTest {
		this.rebuildTests();
		const findFn = (test: IBaseValidationTest) => {
			if (by?.uid) return test.uid === by.uid;
			else if (by?.type) return test.schema.type === by.type;
			return false;
		};
		return this.tests()?.find(findFn);
	}

	public patchTest<T extends IBaseValidationTest>(test: T): void {
		const tests = this.tests();
		const testIndex = tests.findIndex(t => t.uid === test.uid);
		if (testIndex > -1) tests[testIndex] = test;
		patchState(this, {tests});
	}

	public completeTests(...tests: IBaseValidationTest[]): void {
		// complete the tests by arrau order:
		tests.forEach(test => {
			test.results.passed = true;
		});

		// navigate to the next test
		this.navigateToNextTest().then(r => {});
	}

	public navigateToNextTest(): Promise<boolean> {
		const cycleID = innerSignal<ISignCycle>(this.signCycleModel$)?.id;
		if (cycleID) {
			const activeTest = this.getActiveTest();
			const routes: string[] = [];
			if (this.allPassed()) {
				routes.push('sign', cycleID);
			} else {
				routes.push('validations');
				switch (activeTest.schema.type) {
					case ValidationType.Liveness:
						routes.push('liveness', cycleID);
						break;
					case ValidationType.DocumentVerification:
						routes.push('document-reader', cycleID);
						break;
					case ValidationType.FaceMatch:
						routes.push('face-match', cycleID);
						// set the face-match test input data (liveness test results):
						const livenessTestResults = this.getValidationTestBy({type: ValidationType.Liveness})?.as<ILivenessValidatorTest>()?.results.resultData;
						const faceMatchTest = activeTest.as<IFaceMatchValidatorTest>();
						faceMatchTest.inputData = {
							image1: livenessTestResults?.imageData,
							image2: faceMatchTest.schema.imageData,
						};
						// set the test in the store
						this.patchTest(faceMatchTest);
						break;
				}
			}
			return this.router.navigate(routes);
		}
	}
}

// to run angular commant: ng update @ngrx/store@18 by nx cli:
// npx nx update @ngrx/store@18
